import { Building, CreditCard, Database, FileText, Landmark, Users } from "lucide-react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { PageLayout } from "@/components/layout/PageLayout";
import { NavigationSidebar } from "@/components/navigation";
import type { NavItem } from "@/components/navigation";
import { useSidebarState } from "@/components/navigation/hooks/useSidebarState";
import { SidebarBottomMenus } from "@/components/navigation/organisms/SidebarBottomMenus";
import { InvitationsSection } from "@/components/settings/InvitationsSection";
import { OrganizationSettingsForm } from "@/components/settings/OrganizationSettingsForm";
import { UserRolesSection } from "@/components/settings/UserRolesSection";
import { FranklinLogo } from "@/components/ui/franklin-logo";
import { PageSubtitle } from "@/components/ui/page-subtitle";
import { PageTitle } from "@/components/ui/page-title";
import { hasLegacyBillingAccess, useBillingStatus } from "@/domains/billing/BillingStatusContext";
import { BillingBanners } from "@/domains/billing/components/BillingBanners";
import { BillingDetailsPage } from "@/domains/billing/components/BillingDetailsPage";
import { BillingInvoicesPage } from "@/domains/billing/components/BillingInvoicesPage";
import { BillingOverviewPage } from "@/domains/billing/components/BillingOverviewPage";
import { usePermissions } from "@/hooks/usePermissions";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { PageInfoId } from "@/lib/i18n/types/pageInfo";

import { OrganizationUsage } from "@/domains/usage/OrganizationUsage";

type OrgSettingsSection =
  | "general"
  | "users"
  | "usage"
  | "billing"
  | "billing-details"
  | "billing-invoices";

const SIDEBAR_SCOPE = "organization-settings";

const ORG_SETTINGS_PAGE_INFO: Record<OrgSettingsSection, PageInfoId> = {
  general: "orgSettingsGeneral",
  users: "orgSettingsUsers",
  usage: "orgSettingsUsage",
  billing: "billingOverview",
  "billing-details": "billingDetails",
  "billing-invoices": "billingInvoices",
};

function sectionFromPath(pathname: string, search: string): OrgSettingsSection {
  if (pathname === "/billing/details") return "billing-details";
  if (pathname === "/billing/invoices") return "billing-invoices";
  if (pathname === "/billing") return "billing";
  if (pathname === "/organization-settings/users") return "users";
  const section = new URLSearchParams(search).get("section");
  return section === "usage" || section === "users" ? section : "general";
}

export default function OrganizationSettings() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = usePermissions();
  const billing = useBillingStatus();
  const activeSection = sectionFromPath(location.pathname, location.search);
  const isBillingSection = activeSection.startsWith("billing");
  // The two commercial read RPCs are gated on private.billing_org_manager —
  // org_owner or org_admin, read from org_user_roles — and answer 42501 to
  // everyone else, so anyone who is not one of those two must not be offered
  // billing at all. MANAGE_ORG_PERMISSIONS is not the same set: it is a grant,
  // and a role could hold it without holding an organisation-wide role, which
  // would put a nav group on screen whose overview and invoices pages can only
  // fail. An org_admin who is not the owner DOES see everything; the details
  // form is the one that goes read-only for them, with its reason on screen.
  const isBillingManager = permissions.isOrgOwner || permissions.isOrgAdmin;
  const legacy = hasLegacyBillingAccess(billing);
  const showBillingNavigation = isBillingManager && !billing.isLoading && !legacy;

  const { isCollapsed: isNavCollapsed, toggleCollapse: toggleNavCollapse } = useSidebarState({
    buildingId: SIDEBAR_SCOPE,
    defaultCollapsed: false,
  });

  const navItems: NavItem[] = [];
  if (permissions.canManageOrgPermissions) {
    navItems.push(
      {
        id: "general",
        label: t.settings.general,
        icon: Building,
        href: "/organization-settings",
        isActive: activeSection === "general",
      },
      {
        id: "users",
        label: t.common.userManagement,
        icon: Users,
        href: "/organization-settings/users",
        isActive: activeSection === "users",
      },
      {
        id: "usage",
        label: t.settings.usage.title,
        icon: Database,
        href: "/organization-settings?section=usage",
        isActive: activeSection === "usage",
      }
    );
  }
  if (showBillingNavigation) {
    navItems.push(
      { id: "billing-group", label: t.billing.nav.group, isSectionLabel: true },
      {
        id: "billing",
        label: t.billing.nav.overview,
        icon: CreditCard,
        href: "/billing",
        isActive: activeSection === "billing",
      },
      {
        id: "billing-details",
        label: t.billing.nav.details,
        icon: Landmark,
        href: "/billing/details",
        isActive: activeSection === "billing-details",
      },
      {
        id: "billing-invoices",
        label: t.billing.nav.invoices,
        icon: FileText,
        href: "/billing/invoices",
        isActive: activeSection === "billing-invoices",
      }
    );
  }

  // Each section owns the page title so the heading matches what the user is
  // looking at, instead of a generic "Organization settings" on all of them.
  const header: Record<OrgSettingsSection, { title: string; subtitle?: string }> = {
    general: {
      title: t.settings.organizationTitle,
      subtitle: t.settings.organizationSubtitle,
    },
    users: {
      title: t.common.userManagement,
      subtitle: t.settings.userManagementSubtitle,
    },
    usage: { title: t.settings.usage.title, subtitle: t.settings.usage.subtitle },
    billing: {
      title: t.billing.page.overviewTitle,
      subtitle: t.billing.page.overviewSubtitle,
    },
    "billing-details": {
      title: t.billing.page.detailsTitle,
      subtitle: t.billing.page.detailsSubtitle,
    },
    "billing-invoices": {
      title: t.billing.page.invoicesTitle,
    },
  };

  const pageHeader = header[activeSection];

  const billingContent =
    activeSection === "billing-details" ? (
      <BillingDetailsPage />
    ) : activeSection === "billing-invoices" ? (
      <BillingInvoicesPage />
    ) : (
      <BillingOverviewPage />
    );

  // A deep link to a billing page from someone holding MANAGE_ORG_PERMISSIONS but
  // no organisation-wide role: both commercial RPCs would answer 42501, so send
  // them to the section they can actually read instead of rendering an error.
  // `billing.isLoading` is the readiness signal — it stays true until the
  // organisation context resolves, so a cold load never bounces a real owner.
  if (isBillingSection && !billing.isLoading && (!isBillingManager || legacy)) {
    return <Navigate to="/organization-settings" replace />;
  }

  return (
    <PageLayout showHeader={false} customPadding>
      <div className="flex h-screen w-full">
        <NavigationSidebar
          buildingId={SIDEBAR_SCOPE}
          items={navItems}
          logo={
            <FranklinLogo
              size="sm"
              expanded={!isNavCollapsed}
              showBorder={false}
              onClick={() => navigate("/buildings")}
              className="bg-transparent"
            />
          }
          footer={({ isCollapsed }) => <SidebarBottomMenus isCollapsed={isCollapsed} />}
          isCollapsed={isNavCollapsed}
          onToggleCollapse={toggleNavCollapse}
        />
        {/* `relative` contains Radix form controls' absolute hidden inputs — see Settings.tsx. */}
        <div className="relative flex-1 overflow-auto p-6">
          <div className="mx-auto max-w-4xl space-y-8">
            <div className="space-y-1 pt-10 lg:pt-0" data-testid="org-settings-page-header">
              <PageTitle info={ORG_SETTINGS_PAGE_INFO[activeSection]}>{pageHeader.title}</PageTitle>
              {pageHeader.subtitle && <PageSubtitle>{pageHeader.subtitle}</PageSubtitle>}
            </div>

            {isBillingSection && billing.context && <BillingBanners context={billing.context} />}

            {activeSection === "usage" && <OrganizationUsage />}
            {activeSection === "general" && <OrganizationSettingsForm />}
            {activeSection === "users" && (
              // Invitations and user roles are flat sections separated by a
              // hairline — no cards.
              <div className="divide-y divide-border">
                <InvitationsSection />
                <UserRolesSection />
              </div>
            )}
            {isBillingSection && billingContent}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
